/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TestRunDocumentFile } from '../models/test-run-document-file';
import { TestToolService } from '../services/test-tool.service';
import { GenericModalService, ModalSettingsTemplate } from '../../shared/generic-modal/generic-modal.service';
import { LoadingPanelService } from 'src/app/shared/loading-panel/loading-panel.service';
import { SvgIconComponent } from '../../shared/icons/svg-icon.component';
import { VisualCompareComponent } from '../../shared/visual-compare/visual-compare.component';
import { TestToolFileInfoComponent } from '../test-tool-file-info/test-tool-file-info.component';
import { AcceptStateDto } from '../models/test-run-accept-state';
import { TestToolAcceptDifferencesModalComponent } from '../test-tool-accept-differences-modal/test-tool-accept-differences-modal.component';
import { ToastService, ToastType } from 'src/app/shared/toast/toast.service';

@Component({
  selector: 'app-test-tool-visual-compare',
  templateUrl: './test-tool-visual-compare.component.html',
  styleUrls: ['./test-tool-visual-compare.component.scss'],
  imports: [SvgIconComponent, VisualCompareComponent, TestToolFileInfoComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestToolVisualCompareComponent implements OnInit {
  private loadingPanelService = inject(LoadingPanelService);
  private modalService = inject(GenericModalService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private testToolService = inject(TestToolService);
  private toastService = inject(ToastService);

  private noFile = new TestRunDocumentFile({ Content: '(No file)', ContentType: 'text/plain', Encoding: 'UTF8' });

  public testRunId = signal(0);
  public testRunDocumentsId = signal(0);
  public documentIdForActual = signal(0);
  public parsed = signal(false);
  public testCaseId = signal(0);
  public testCaseName = signal('');
  public testCaseInterchangeId = signal(0);
  
  public expected = signal(this.noFile);
  public actual = signal(this.noFile);
  public acceptState = signal<AcceptStateDto>({ TestCaseInterchangeExpectedOutputFilesId: 0, TestRunInterchangeId: 0, TestRunDocumentId: 0, Failed: false, Accepted: false, Note: '' });

  public currentResultNumber = computed(() =>
    this.navigationTestRunDocumentMetadata
      ? this.navigationTestRunDocumentMetadata.find(item => item.TestRunDocumentId === this.testRunDocumentsId())?.SortOrder as number ?? 0
      : 0
  );
  public switchParsedButtonText = computed(() => this.parsed() ? 'Switch to unparsed' : 'Switch to parsed');
  public canAcceptDifferences = computed(() => this.acceptState().Failed && !this.acceptState().Accepted);
  public canRejectDifferences = computed(() => this.acceptState().Accepted);
  public canNavigateToTestCase = computed(() => this.testCaseId() > 0 && this.testCaseInterchangeId() > 0 && this.testCaseName() !== '');

  private navigationTestRunDocumentMetadata = [];
  public language = '';

  ngOnInit(): void {
    this.navigationTestRunDocumentMetadata = history.state.navigationTestRunDocumentMetadata;

    // Subscribe to route param changes and update signals. Angular will not update input
    // signals when navigating to the same component with new parameters. It is necessary
    // to manually listen for parameter changes and update signals in response.
    this.route.params.subscribe(params => {
      this.testRunId.set(+params['testRunId'] || 0);
      this.testRunDocumentsId.set(+params['testRunDocumentsId'] || 0);
      this.documentIdForActual.set(+params['documentIdForActual'] || 0);
      this.parsed.set(params['parsed'] === 'true' || params['parsed'] === true);
      this.testCaseId.set(+params['testCaseId'] || 0);
      this.testCaseName.set(String(params['testCaseName'] || ''));
      this.testCaseInterchangeId.set(+params['testCaseInterchangeId'] || 0);
      void this.refresh();
    });
  }

  async refresh(): Promise<void> {
    try {
      this.loadingPanelService.showLoadingPanelDirect('Loading...');

      const [expected, actual, acceptState] = await Promise.all([
        this.testToolService.getTestRunDocumentFile(this.testRunDocumentsId(), false, this.parsed()),
        this.testToolService.getTestRunDocumentFile(this.testRunDocumentsId(), true, this.parsed()),
        this.testToolService.getAcceptStateForTestRunDocument(this.testRunDocumentsId()),
      ]);

      this.expected.set(expected ?? this.noFile);
      this.actual.set(actual ?? this.noFile);
      this.acceptState.set(acceptState);
      const displayAs = this.expected().DisplayAs.toLowerCase();
      switch (displayAs) {
        case 'xml':
        case 'json':
          this.language = displayAs;
          break;

        default:
          this.language = 'plaintext';
          break;
      }
    } finally {
      this.loadingPanelService.hideLoadingPanel(); 
    }
  }

  openDocuments() {
    const baseUrl = window.location.origin;
    const deepLink = `${baseUrl}/tracking/result/details;documentIds=${this.documentIdForActual()}`;
    window.open(deepLink, '_blank');
  }

  async rerunTestRun(): Promise<void> {
    try {
        await this.modalService.confirmDirect('Rerun test run', 'This will create a completely new test run. Continue?')
        try{
          this.loadingPanelService.showLoadingPanelDirect('Starting test run...');
          const runId = await this.testToolService.rerunTestRun(this.testRunId());
          this.toastService.showToast('Test runs started successfully', ToastType.success);
          await this.router.navigate(['/test-tool/test-runs/test-run-results', { testRunId: runId }]);
        }
        catch{
          this.toastService.showToast('Failed to start test run...', ToastType.error);
        }
        finally {
          this.loadingPanelService.hideLoadingPanel();
        }
      } catch {
      // User cancelled or error occurred; do nothing
    }
  }

  downloadAllTestRunFiles() {
    void this.testToolService.downloadAllTestRunFiles(this.testRunDocumentsId());
  }

  viewTestCaseRowEvent(): void {
    void this.router.navigate(['/test-tool/test-cases/test-case-interchanges', {
      testCaseId: this.testCaseId(),
      testCaseName: this.testCaseName(),
      expandInterchangeId: this.testCaseInterchangeId()
    }]);
  }

  async acceptDifferences(): Promise<void> {
    try {
      const modalSettings = new ModalSettingsTemplate(undefined, 'static');
      const newAcceptState: AcceptStateDto = {
        TestCaseInterchangeExpectedOutputFilesId: this.acceptState().TestCaseInterchangeExpectedOutputFilesId,
        TestRunInterchangeId: this.acceptState().TestRunInterchangeId,
        TestRunDocumentId: this.acceptState().TestRunDocumentId,
        Failed: this.acceptState().Failed,
        Accepted: true,
        Note: '',
      };
      newAcceptState.Note = await this.modalService.openComponentWithInitialize<string, TestToolAcceptDifferencesModalComponent>(TestToolAcceptDifferencesModalComponent, modalSettings, dialog => {
        dialog.isAccept.set(true);
        dialog.comment.set(this.acceptState().Note);
      });

      try {
        this.loadingPanelService.showLoadingPanelDirect('Accepting differences...');
        const newStates = await this.testToolService.updateAcceptState([newAcceptState]);
        this.acceptState.set(newStates[0]);
      }
      finally {
        this.loadingPanelService.hideLoadingPanel();
      }
    }
    catch {
      // User cancelled or error occurred; do nothing
      return;
    }
  }

  async rejectDifferences(): Promise<void> {
    try {
      const modalSettings = new ModalSettingsTemplate(undefined, 'static');
      const newAcceptState: AcceptStateDto = {
        TestCaseInterchangeExpectedOutputFilesId: this.acceptState().TestCaseInterchangeExpectedOutputFilesId,
        TestRunInterchangeId: this.acceptState().TestRunInterchangeId,
        TestRunDocumentId: this.acceptState().TestRunDocumentId,
        Failed: this.acceptState().Failed,
        Accepted: false,
        Note: '',
      };
      newAcceptState.Note = await this.modalService.openComponentWithInitialize<string, TestToolAcceptDifferencesModalComponent>(TestToolAcceptDifferencesModalComponent, modalSettings, dialog => {
        dialog.isAccept.set(false);
        dialog.comment.set(this.acceptState().Note);
      });

      try {
        this.loadingPanelService.showLoadingPanelDirect('Rejecting differences...');
        const newStates = await this.testToolService.updateAcceptState([newAcceptState]);
        this.acceptState.set(newStates[0]);
      }
      finally {
        this.loadingPanelService.hideLoadingPanel();
      }
    }
    catch {
      // User cancelled or error occurred; do nothing
      return;
    }
  }

  previousResultNumber() {
    this.changeResultNumber(false);
  }

  nextResultNumber() {
    this.changeResultNumber(true);
  }

  switchParsedState() {
    // Update URL with new IDs. Does not add to browser back history.
    void this.router.navigate(
      ['/test-tool/test-runs/test-run-results/compare', { 
        testRunId: this.testRunId(), 
        testRunDocumentsId: this.testRunDocumentsId(), 
        documentIdForActual: this.documentIdForActual(), 
        parsed: !this.parsed(),
        testCaseId: this.testCaseId(),
        testCaseName: this.testCaseName(),
        testCaseInterchangeId: this.testCaseInterchangeId()
      }],
      { replaceUrl: true, state: { navigationTestRunDocumentMetadata: this.navigationTestRunDocumentMetadata } });
  }

  changeResultNumber(goNext: boolean) {
    const index = this.navigationTestRunDocumentMetadata.findIndex(item => item.SortOrder === this.currentResultNumber());

    if (index !== -1) {
      let newIndex = index;

      if (goNext) {
        // Move to the next result, wrap around if at the end of the list
        newIndex = (index + 1) % this.navigationTestRunDocumentMetadata.length;
      } else {
        // Move to the previous result, wrap around if at the beginning of the list
        newIndex = (index - 1 + this.navigationTestRunDocumentMetadata.length) % this.navigationTestRunDocumentMetadata.length;
      }

      // Update URL with new IDs. Does not add to browser back history.
      const result = this.navigationTestRunDocumentMetadata[newIndex];
      void this.router.navigate(
        ['/test-tool/test-runs/test-run-results/compare', { 
          testRunId: this.testRunId(), 
          testRunDocumentsId: result.TestRunDocumentId, 
          documentIdForActual: result.DocumentIdForActual, 
          parsed: this.parsed(),
          testCaseId: result.TestCaseId,
          testCaseName: result.TestCaseName,
          testCaseInterchangeId: result.TestCaseInterchangeId,
        }],
        { replaceUrl: true, state: { navigationTestRunDocumentMetadata: this.navigationTestRunDocumentMetadata } });
    }
  }
}
